class DeliveryModal extends HTMLElement {
  constructor() {
    super()

    this.init(this._getDeliveryModalContent)
  }

  init() {
    this._getDeliveryModalContent()
  }

  async _hydrateModalDelivery() {
    const newDeliveryMethods = (
      window.deliveryInfoCache?.find(
        (variant) => variant.sku.includes('NW-') && !!variant?.delivery_methods?.length
      )?.delivery_methods ?? []
    )
      .filter((method) => !!method?.new_prices?.length)
      .sort((a, b) => Number(a.new_prices?.[0]?.price?.amount) - Number(b.new_prices?.[0]?.price?.amount))
    const secondHandDeliveryMethods = (
      window.deliveryInfoCache?.find(
        (variant) => !variant.sku.startsWith('NW-') && !!variant?.delivery_methods?.length
      )?.delivery_methods ?? []
    )
      .filter((method) => !!method?.second_hand_prices?.length)
      .sort(
        (a, b) => Number(a.second_hand_prices?.[0]?.price?.amount) - Number(b.second_hand_prices?.[0]?.price?.amount)
      )

    if (!this._deliverySectionTemplate) return

    if (newDeliveryMethods.length) {
      this.newParentContainer.classList.remove('jc-hidden')
    }

    if (secondHandDeliveryMethods.length) {
      this.secondHandParentContainer.classList.remove('jc-hidden')
    }

    this.secondHandContainer?.replaceChildren()
    this.newContainer?.replaceChildren()

    newDeliveryMethods.forEach((method) => {
      const newCard = this._buildCarrierCard(method, 'new_prices')
      if (newCard) {
        this.newContainer?.appendChild(newCard)
      }
    })

    secondHandDeliveryMethods.forEach((method) => {
      const secondHandCard = this._buildCarrierCard(method, 'second_hand_prices')
      if (secondHandCard) {
        this.secondHandContainer?.appendChild(secondHandCard)
      }
    })
  }

  async _getDeliveryModalContent() {
    if (this._deliverySectionTemplate) return this._hydrateModalDelivery()

    try {
      const fetchedSection = await fetch(window.Shopify.routes.root + 'search/?sections=ajax-delivery-modal')
      const section = await fetchedSection.json()
      const sectionHtml = section?.['ajax-delivery-modal'] || Object.values(section || {})?.[0]

      if (!sectionHtml) return

      const template = document.createElement('template')
      template.innerHTML = sectionHtml.trim()
      this._deliverySectionTemplate = template

      this._hydrateModalDelivery()
    } catch (error) {
      console.error('Error fetching delivery modal section', error)
    }
  }

  _buildCarrierCard(method, priceKey) {
    const prices = method?.[priceKey]

    if (!prices?.length) return null

    const card = this._deliverySectionTemplate.content.firstElementChild.cloneNode(true)
    const datesEl = card.querySelector('.js-carrier-delivery-dates')
    const logoEl = card.querySelector('.js-carrier-logo')
    const nameEl = card.querySelector('.js-carrier-name')
    const linesWrapper = card.querySelector('.js-carrier-lines-wrapper')
    const lineTemplate = card.querySelector('.js-carrier-line')

    if (!datesEl || !nameEl || !linesWrapper || !lineTemplate) return null

    const minDays = method?.estimated_delivery_min || 0
    const maxDays = method?.estimated_delivery_max || 0

    datesEl.textContent = this._formatDeliveryDates(minDays, maxDays)
    nameEl.textContent = method?.method || ''

    if (logoEl) {
      if (method?.logo) {
        logoEl.src = method.logo
        logoEl.classList.remove('jc-hidden')
      } else {
        logoEl.classList.add('jc-hidden')
      }
    }

    linesWrapper.replaceChildren()

    prices.forEach((priceItem) => {
      const line = lineTemplate.cloneNode(true)
      const thresholdEl = line.querySelector('.js-carrier-threshold')
      const priceEl = line.querySelector('.js-carrier-price')
      const currencyEl = line.querySelector('.js-carrier-currency')

      if (thresholdEl) {
        thresholdEl.textContent = priceItem?.label || ''
      }

      if (priceEl) {
        const amount = Number(priceItem?.price?.amount ?? '')
        let priceText = ''

        if (Number.isFinite(amount) && !Number.isNaN(amount)) {
          priceText =
            amount === 0
              ? window.languages?.deliveryFree || ''
              : amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }

        priceEl.textContent = priceText

        if (currencyEl) {
          if (amount === 0 || Number.isNaN(amount)) {
            currencyEl.classList.add('jc-hidden')
          } else {
            currencyEl.classList.remove('jc-hidden')
          }
        }
      }

      linesWrapper.appendChild(line)
    })

    return card
  }

  _formatDeliveryDates(minDays, maxDays) {
    const startDate = this._formatDate(minDays)
    const endDate = this._formatDate(maxDays)
    const template = window.languages?.deliveredBetweenDates || ''

    if (template) {
      return template.replace('{{ start_date }}', startDate).replace('{{ end_date }}', endDate)
    }

    return `${startDate} - ${endDate}`
  }

  _formatDate(daysToAdd = 0) {
    const date = new Date()
    date.setDate(date.getDate() + daysToAdd)
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  }

  get secondHandContainer() {
    if (!this._secondHandContainer) {
      this._secondHandContainer = this.querySelector('.js-second-hand-carriers')
    }
    return this._secondHandContainer
  }

  get newContainer() {
    if (!this._newContainer) {
      this._newContainer = this.querySelector('.js-new-carriers')
    }
    return this._newContainer
  }

  get secondHandParentContainer() {
    if (!this._secondHandParentContainer) {
      this._secondHandParentContainer = this.querySelector('.js-second-hand-carriers-parent')
    }
    return this._secondHandParentContainer
  }

  get newParentContainer() {
    if (!this._newParentContainer) {
      this._newParentContainer = this.querySelector('.js-new-carriers-parent')
    }
    return this._newParentContainer
  }
}

if (!customElements.get('delivery-modal')) {
  customElements.define('delivery-modal', DeliveryModal)
}
